
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Bold,
  Italic,
  List,
  Link,
  AtSign,
  Image
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface CommentEditorProps {
  onSubmit: (content: string) => void;
  placeholder?: string;
  buttonText?: string;
  initialValue?: string;
}

// Simple format tags for the comment editor
type FormatTag = {
  tag: string;
  start: string;
  end: string;
  icon: React.ReactNode;
  tooltip: string;
};

const CommentEditor: React.FC<CommentEditorProps> = ({
  onSubmit,
  placeholder = "Write your comment...",
  buttonText = "Submit",
  initialValue = ""
}) => {
  const [content, setContent] = useState(initialValue);
  const [textareaRef, setTextareaRef] = useState<HTMLTextAreaElement | null>(null);
  
  const formatTags: FormatTag[] = [
    {
      tag: 'bold',
      start: '**',
      end: '**',
      icon: <Bold className="h-4 w-4" />,
      tooltip: 'Bold'
    },
    {
      tag: 'italic',
      start: '_',
      end: '_',
      icon: <Italic className="h-4 w-4" />,
      tooltip: 'Italic'
    },
    {
      tag: 'list',
      start: '\n- ',
      end: '',
      icon: <List className="h-4 w-4" />,
      tooltip: 'Bullet list'
    },
    {
      tag: 'link',
      start: '[',
      end: '](url)',
      icon: <Link className="h-4 w-4" />,
      tooltip: 'Add link'
    },
    {
      tag: 'mention',
      start: '@',
      end: '',
      icon: <AtSign className="h-4 w-4" />,
      tooltip: 'Mention someone'
    }
  ];

  const handleFormatText = (format: FormatTag) => {
    if (!textareaRef) return;
    
    const start = textareaRef.selectionStart;
    const end = textareaRef.selectionEnd;
    const selectedText = content.substring(start, end);
    
    const beforeText = content.substring(0, start);
    const afterText = content.substring(end);
    
    // If text is selected, wrap it with format tags
    if (selectedText) {
      setContent(beforeText + format.start + selectedText + format.end + afterText);
    } else {
      // If no text is selected, just insert format tags
      setContent(beforeText + format.start + format.end + afterText);
      
      // Set cursor position between tags
      setTimeout(() => {
        if (textareaRef) {
          const newCursorPos = start + format.start.length;
          textareaRef.focus();
          textareaRef.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (content.trim()) {
      onSubmit(content);
      setContent('');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <div className="border border-input rounded-md">
        <Textarea 
          ref={(ref) => setTextareaRef(ref)}
          placeholder={placeholder} 
          value={content} 
          onChange={(e) => setContent(e.target.value)} 
          className="border-0 focus-visible:ring-0 min-h-[120px] resize-y"
        />
        
        <div className="flex items-center border-t p-2 bg-muted/20">
          <TooltipProvider>
            <div className="flex gap-1">
              {formatTags.map((format) => (
                <Tooltip key={format.tag}>
                  <TooltipTrigger asChild>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 w-8 p-0"
                      onClick={() => handleFormatText(format)}
                    >
                      {format.icon}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{format.tooltip}</TooltipContent>
                </Tooltip>
              ))}
            </div>
          </TooltipProvider>
          
          <div className="ml-auto">
            <Button type="submit" size="sm" className="h-8" disabled={!content.trim()}>
              {buttonText}
            </Button>
          </div>
        </div>
      </div>
      
      <div className="text-xs text-muted-foreground">
        Formatting: Use **bold**, _italic_, or @mention someone
      </div>
    </form>
  );
};

export default CommentEditor;
